// services/ReportService.js - ReactJS (web)
import api from '../api/api';

class ReportService {
  // ==================== PROVIDER ROUTES ====================

  /**
   * Create a new report from a provider
   * POST /api/reports/create-report
   */
  async createProviderReport(reportData) {
    try {
      const response = await api.post('/reports/create-report', reportData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get provider's own reports
   * GET /api/reports/my-reports
   */
  async getProviderReports(params = {}) {
    try {
      const response = await api.get('/reports/my-reports', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get report by ID for provider (only if they own it)
   * GET /api/reports/provider/:reportId
   */
  async getReportForProvider(reportId) {
    try {
      const response = await api.get(`/reports/provider/${reportId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Provider replies to report communication
   * POST /api/reports/:reportId/reply
   */
  async providerReply(reportId, message) {
    try {
      const response = await api.post(`/reports/${reportId}/reply`, { message });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Add feedback to resolved report
   * POST /api/reports/:reportId/feedback
   */
  async addFeedback(reportId, rating, comment) {
    try {
      const response = await api.post(`/reports/${reportId}/feedback`, { rating, comment });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== ADMIN ROUTES ====================

  /**
   * Get all reports with filters (admin only)
   * GET /api/reports/
   */
  async getAllReports(params = {}) {
    try {
      const response = await api.get('/reports/', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get report by ID (admin only)
   * GET /api/reports/:reportId 
   */
  async getReportById(reportId) {
    try {
      const response = await api.get(`/reports/${reportId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Assign report to admin
   * PATCH /api/reports/:reportId/assign
   * ✅ FIXED: adminId is optional - if not provided, uses current admin
   */
  async assignReportToAdmin(reportId, adminId = null) {
    try {
      const payload = {};
      // Only include adminId if provided
      if (adminId) {
        payload.adminId = adminId;
      }
      const response = await api.patch(`/reports/${reportId}/assign`, payload);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update report status
   * PATCH /api/reports/:reportId/status
   */
  async updateReportStatus(reportId, status) {
    try {
      const response = await api.patch(`/reports/${reportId}/status`, { status });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Add admin response to report
   * POST /api/reports/:reportId/response
   */
  async addAdminResponse(reportId, message, actionTaken) {
    try {
      const response = await api.post(`/reports/${reportId}/response`, { message, actionTaken });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Add internal admin note
   * POST /api/reports/:reportId/notes
   */
  async addAdminNote(reportId, note) {
    try {
      const response = await api.post(`/reports/${reportId}/notes`, { note });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Add communication with provider
   * POST /api/reports/:reportId/communicate
   */
  async communicateWithProvider(reportId, message, isInternal = false) {
    try {
      const response = await api.post(`/reports/${reportId}/communicate`, { message, isInternal });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Mark report as duplicate
   * PATCH /api/reports/:reportId/duplicate
   */
  async markAsDuplicate(reportId, duplicateOfId) {
    try {
      const response = await api.patch(`/reports/${reportId}/duplicate`, { duplicateOfId });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Resolve report with action taken
   * PATCH /api/reports/:reportId/resolve
   */
  async resolveReport(reportId, actionTaken, description) {
    try {
      const response = await api.patch(`/reports/${reportId}/resolve`, { actionTaken, description });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Dismiss report
   * PATCH /api/reports/:reportId/dismiss
   */
  async dismissReport(reportId, reason) {
    try {
      const response = await api.patch(`/reports/${reportId}/dismiss`, { reason });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get reports by provider business name
   * GET /api/reports/business/:businessName
   */
  async getReportsByBusinessName(businessName, params = {}) {
    try {
      const response = await api.get(`/reports/business/${businessName}`, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== SUPER ADMIN ROUTES ====================

  /**
   * Escalate report to super admin
   * PATCH /api/reports/:reportId/escalate
   */
  async escalateReport(reportId, reason) {
    try {
      const response = await api.patch(`/reports/${reportId}/escalate`, { reason });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get report statistics
   * GET /api/reports/statistics
   */
  async getReportStatistics() {
    try {
      const response = await api.get('/reports/statistics');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get detailed report analytics
   * GET /api/reports/analytics
   */
  async getReportAnalytics() {
    try {
      const response = await api.get('/reports/analytics');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get unassigned reports count
   * GET /api/reports/unassigned/count
   */
  async getUnassignedCount() {
    try {
      const response = await api.get('/reports/unassigned/count');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get urgent reports count
   * GET /api/reports/urgent/count
   */
  async getUrgentCount() {
    try {
      const response = await api.get('/reports/urgent/count');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Handle API errors
   */
  handleError(error) {
    let errorMessage = 'An unexpected error occurred';
    let statusCode = error.response?.status || 500;
    let data = error.response?.data || null;
    
    // Check if error response exists
    if (error.response) {
      // Try different paths for error message
      if (error.response.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.response.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response.data?.error) {
        errorMessage = typeof error.response.data.error === 'string' 
          ? error.response.data.error 
          : JSON.stringify(error.response.data.error);
      } else if (error.response.statusText) {
        errorMessage = error.response.statusText;
      }
      
      // Special handling for 404 Not Found
      if (statusCode === 404) {
        errorMessage = 'Resource not found. Please check the URL or ID.';
      }
      
      // Special handling for 400 Bad Request
      if (statusCode === 400) {
        errorMessage = 'Invalid request. Please check your input.';
        if (error.response.data?.data?.error?.message) {
          errorMessage = error.response.data.data.error.message;
        }
      }
      
      // Special handling for 403 Forbidden
      if (statusCode === 403) {
        errorMessage = 'You do not have permission to perform this action.';
      }
      
      // Special handling for 401 Unauthorized
      if (statusCode === 401) {
        errorMessage = 'Please log in to continue.';
      }
    } else if (error.request) {
      // Request was made but no response received
      errorMessage = 'No response from server. Please check your connection.';
    } else {
      // Something else happened
      errorMessage = error.message || 'An unexpected error occurred';
    }
    
    console.error('[ReportService] ❌ Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: statusCode,
      message: errorMessage,
      data: data
    });
    
    return {
      success: false,
      message: errorMessage,
      statusCode: statusCode,
      data: data
    };
  }

  /**
   * Get report types
   */
  getReportTypes() {
    return [
      { value: 'app_issue', label: 'App Issue' },
      { value: 'payment_issue', label: 'Payment Issue' },
      { value: 'account_issue', label: 'Account Issue' },
      { value: 'feature_request', label: 'Feature Request' },
      { value: 'bug_report', label: 'Bug Report' },
      { value: 'support_request', label: 'Support Request' },
      { value: 'security_concern', label: 'Security Concern' },
      { value: 'content_issue', label: 'Content Issue' },
      { value: 'other', label: 'Other' }
    ];
  }

  /**
   * Get report categories
   */
  getReportCategories() {
    return [
      { value: 'technical', label: 'Technical' },
      { value: 'payment', label: 'Payment' },
      { value: 'account', label: 'Account' },
      { value: 'feature', label: 'Feature' },
      { value: 'bug', label: 'Bug' },
      { value: 'support', label: 'Support' },
      { value: 'security', label: 'Security' },
      { value: 'content', label: 'Content' },
      { value: 'other', label: 'Other' }
    ];
  }

  /**
   * Get report statuses
   */
  getReportStatuses() {
    return [
      { value: 'pending', label: 'Pending' },
      { value: 'under_review', label: 'Under Review' },
      { value: 'in_progress', label: 'In Progress' },
      { value: 'resolved', label: 'Resolved' },
      { value: 'dismissed', label: 'Dismissed' }
    ];
  }

  /**
   * Get severity levels
   */
  getSeverityLevels() {
    return [
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
      { value: 'critical', label: 'Critical' }
    ];
  }

  /**
   * Get priority levels
   */
  getPriorityLevels() {
    return [
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
      { value: 'urgent', label: 'Urgent' }
    ];
  }

  /**
   * Validate report data
   */
  validateReportData(reportData) {
    const errors = [];

    if (!reportData.type) {
      errors.push('Report type is required');
    }

    if (!reportData.category) {
      errors.push('Report category is required');
    }

    if (!reportData.subject || reportData.subject.trim().length === 0) {
      errors.push('Subject is required');
    } else if (reportData.subject.length < 5) {
      errors.push('Subject must be at least 5 characters');
    }

    if (!reportData.description || reportData.description.trim().length === 0) {
      errors.push('Description is required');
    } else if (reportData.description.length < 20) {
      errors.push('Description must be at least 20 characters');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Get status badge color
   */
  getStatusBadgeColor(status) {
    const colors = {
      pending: '#FFA500',
      under_review: '#3498DB',
      in_progress: '#9B59B6',
      resolved: '#2ECC71',
      dismissed: '#95A5A6'
    };
    return colors[status] || '#95A5A6';
  }

  /**
   * Get severity badge color
   */
  getSeverityBadgeColor(severity) {
    const colors = {
      low: '#2ECC71',
      medium: '#F1C40F',
      high: '#E67E22',
      critical: '#E74C3C'
    };
    return colors[severity] || '#95A5A6';
  }

  /**
   * Get status label
   */
  getStatusLabel(status) {
    const labels = {
      pending: 'Pending',
      under_review: 'Under Review',
      in_progress: 'In Progress',
      resolved: 'Resolved',
      dismissed: 'Dismissed'
    };
    return labels[status] || status;
  }

  /**
   * Get severity label
   */
  getSeverityLabel(severity) {
    const labels = {
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      critical: 'Critical'
    };
    return labels[severity] || severity;
  }

  /**
   * Get priority label
   */
  getPriorityLabel(priority) {
    const labels = {
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      urgent: 'Urgent'
    };
    return labels[priority] || priority;
  }

  /**
   * Format report for display
   */
  formatReport(report) {
    if (!report) return null;

    return {
      ...report,
      id: report._id,
      typeLabel: this.getTypeLabel(report.type),
      categoryLabel: this.getCategoryLabel(report.category),
      statusLabel: this.getStatusLabel(report.status),
      statusColor: this.getStatusBadgeColor(report.status),
      severityLabel: this.getSeverityLabel(report.severity),
      severityColor: this.getSeverityBadgeColor(report.severity),
      priorityLabel: this.getPriorityLabel(report.priority),
      createdAt: report.createdAt ? new Date(report.createdAt) : null,
      updatedAt: report.updatedAt ? new Date(report.updatedAt) : null,
      resolvedAt: report.resolution?.resolvedAt ? new Date(report.resolution.resolvedAt) : null,
      timeAgo: report.createdAt ? this.getTimeAgo(report.createdAt) : null,
      hasAdminResponse: report.adminResponse ? true : false,
      hasInternalNotes: report.adminNotes && report.adminNotes.length > 0,
      noteCount: report.adminNotes?.length || 0,
      communicationCount: report.communication?.length || 0,
      isResolved: report.status === 'resolved',
      isDismissed: report.status === 'dismissed',
      isPending: report.status === 'pending',
      isUrgent: report.priority === 'urgent' || report.priority === 'high'
    };
  }

  /**
   * Get type label
   */
  getTypeLabel(type) {
    const labels = {
      app_issue: 'App Issue',
      payment_issue: 'Payment Issue',
      account_issue: 'Account Issue',
      feature_request: 'Feature Request',
      bug_report: 'Bug Report',
      support_request: 'Support Request',
      security_concern: 'Security Concern',
      content_issue: 'Content Issue',
      other: 'Other'
    };
    return labels[type] || type;
  }

  /**
   * Get category label
   */
  getCategoryLabel(category) {
    const labels = {
      technical: 'Technical',
      payment: 'Payment',
      account: 'Account',
      feature: 'Feature',
      bug: 'Bug',
      support: 'Support',
      security: 'Security',
      content: 'Content',
      other: 'Other'
    };
    return labels[category] || category;
  }

  /**
   * Get time ago string
   */
  getTimeAgo(date) {
    if (!date) return null;
    const now = new Date();
    const diff = now - new Date(date);
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    if (weeks < 4) return `${weeks}w ago`;
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  }

  /**
   * Build query params for report search
   */
  buildQueryParams(filters = {}) {
    const params = {};

    if (filters.status) params.status = filters.status;
    if (filters.type) params.type = filters.type;
    if (filters.category) params.category = filters.category;
    if (filters.severity) params.severity = filters.severity;
    if (filters.priority) params.priority = filters.priority;
    if (filters.search) params.search = filters.search;
    if (filters.fromDate) params.fromDate = filters.fromDate;
    if (filters.toDate) params.toDate = filters.toDate;
    if (filters.page) params.page = filters.page;
    if (filters.limit) params.limit = filters.limit;
    if (filters.sortBy) params.sortBy = filters.sortBy;
    if (filters.sortOrder) params.sortOrder = filters.sortOrder;
    if (filters.assignedTo) params.assignedTo = filters.assignedTo;

    return params;
  }

  /**
   * Get report summary
   */
  getReportSummary(report) {
    if (!report) return '';
    const maxLength = 150;
    const text = report.description || '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }
}

export default new ReportService();